import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ColumnFilterWrapper } from "./ColumnFilterWrapper";

const { RangePicker } = DatePicker;

interface DateRange {
  from?: string;
  to?: string;
}

interface DateRangeFilterProps {
  props: { confirm: () => void; close: () => void };
  title?: string;
  placeholder?: string;
  selectedRange?: DateRange;
  handlePropsChange: (props: { range?: DateRange }) => void;
  before?: React.ReactNode;
  filterCount?: number;
  sortActive?: boolean;
  sortDirection?: "asc" | "desc";
  onSortChange?: (dir?: "asc" | "desc") => void;
  onReset?: () => void;
}

export default function DateRangeFilter({
  props,
  title,
  placeholder,
  selectedRange,
  handlePropsChange,
  before,
  filterCount,
  sortActive,
  sortDirection,
  onSortChange,
  onReset,
}: DateRangeFilterProps) {
  const [value, setValue] = useState<[Dayjs | null, Dayjs | null]>([
    selectedRange?.from ? dayjs(selectedRange.from) : null,
    selectedRange?.to ? dayjs(selectedRange.to) : null,
  ]);

  const cleanPlaceholder = placeholder
    ? placeholder.replace(/^(Select|Enter)\s+/i, "").replace(/Date\s+Range/i, "").trim()
    : "";
  const displayTitle =
    title ||
    (cleanPlaceholder
      ? cleanPlaceholder.charAt(0).toUpperCase() + cleanPlaceholder.slice(1)
      : undefined);

  const onChange = (dates: any) => {
    let safeDates: [Dayjs | null, Dayjs | null] = [null, null];
    if (Array.isArray(dates) && dates.length === 2) {
      safeDates = [dates[0]?.startOf("day") || null, dates[1]?.endOf("day") || null];
    }
    setValue(safeDates);
  };

  const handleApply = () => {
    handlePropsChange({
      range: {
        from: value[0]?.toISOString(),
        to: value[1] ? value[1].endOf("day").toISOString() : undefined,
      },
    });
    props.confirm();
  };

  const handleResetInternal = () => {
    setValue([null, null]);
    handlePropsChange({ range: undefined });
    if (onReset) onReset();
    props.confirm();
  };

  const isFiltered = !!(selectedRange?.from || selectedRange?.to);

  return (
    <ColumnFilterWrapper
      title={displayTitle}
      filterCount={filterCount ?? (isFiltered ? 1 : 0)}
      isFiltered={isFiltered}
      sortActive={sortActive}
      sortDirection={sortDirection}
      onSortChange={onSortChange}
      confirm={props.confirm}
      close={props.close}
      onApply={handleApply}
      onReset={handleResetInternal}
    >
      {before}
      <div style={{ padding: "4px 0" }}>
        <p style={{ margin: "0 0 6px 0", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ba3b2" }}>
          Range
        </p>
        <RangePicker
          value={value}
          onChange={onChange}
          style={{ width: "100%" }}
          allowEmpty
          placeholder={
            placeholder
              ? ([`${placeholder} From`, `${placeholder} To`] as [
                  string,
                  string
                ])
              : undefined
          }
        />
      </div>
    </ColumnFilterWrapper>
  );
}
